import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Gift,
  Users,
  DollarSign,
  Copy,
  Check,
  Share2,
  ArrowRight,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Mail,
  Twitter,
  Linkedin,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Referrals() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [credits, setCredits] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [statsRes, creditsRes] = await Promise.all([
        axios.get(`${API}/api/referrals/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/referrals/credits`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setCredits(creditsRes.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Referral link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Join me on Clear Defense AI - Get 1 Month Free!');
    const body = encodeURIComponent(
      `I've been using Clear Defense AI for our cybersecurity needs and thought you might find it useful too.\n\n` +
      `Sign up using my referral link and we both get 1 month free!\n\n` +
      `${stats?.referral_link}\n\n` +
      `Clear Defense AI offers multi-layer protection including DNS filtering, email security, file scanning, and more - all powered by AI.`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareViaTwitter = () => {
    const text = encodeURIComponent(
      `Protect your business with AI-powered cybersecurity! Sign up for Clear Defense AI using my referral link and we both get 1 month free: ${stats?.referral_link}`
    );
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };

  const shareViaLinkedIn = () => {
    const url = encodeURIComponent(stats?.referral_link || '');
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'signed_up':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30"><Clock className="w-3 h-3 mr-1" />Pending Payment</Badge>;
      case 'expired':
        return <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Gift className="w-8 h-8 text-purple-500" />
            Referral Program
          </h1>
          <p className="text-slate-400 mt-1">
            Share Clear Defense AI and earn free months
          </p>
        </div>
      </div>

      {/* How It Works Banner */}
      <Card className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/30">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-purple-400" />
            <h2 className="text-lg font-semibold text-white">How It Works</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold">1</div>
              <div>
                <p className="font-medium text-white">Share Your Link</p>
                <p className="text-sm text-slate-400">Send your unique referral link to colleagues and friends</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold">2</div>
              <div>
                <p className="font-medium text-white">They Sign Up</p>
                <p className="text-sm text-slate-400">They get 1 month free when they sign up using your link</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold">3</div>
              <div>
                <p className="font-medium text-white">You Earn Credit</p>
                <p className="text-sm text-slate-400">You get 1 month free when they make their first payment</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.total_referrals || 0}</p>
                <p className="text-xs text-slate-400">Total Referrals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.successful_referrals || 0}</p>
                <p className="text-xs text-slate-400">Successful</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.pending_referrals || 0}</p>
                <p className="text-xs text-slate-400">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Gift className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{credits?.active_balance_months || 0}</p>
                <p className="text-xs text-slate-400">Months Earned</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Referral Link Card */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Share2 className="w-5 h-5 text-blue-500" />
                Your Referral Link
              </CardTitle>
              <CardDescription>
                Share this link with others to earn free months
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Referral Code */}
                <div className="flex items-center gap-2 p-3 bg-slate-800 rounded-lg">
                  <span className="text-slate-400 text-sm">Your Code:</span>
                  <span className="font-mono font-bold text-xl text-white">{stats?.referral_code}</span>
                </div>

                {/* Referral Link */}
                <div className="flex gap-2">
                  <Input
                    value={stats?.referral_link || ''}
                    readOnly
                    className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                  />
                  <Button
                    onClick={() => copyToClipboard(stats?.referral_link)}
                    className={copied ? "bg-emerald-600" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>

                {/* Share Buttons */}
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={shareViaEmail} className="border-slate-700">
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                  <Button variant="outline" size="sm" onClick={shareViaTwitter} className="border-slate-700">
                    <Twitter className="w-4 h-4 mr-2" />
                    Twitter
                  </Button>
                  <Button variant="outline" size="sm" onClick={shareViaLinkedIn} className="border-slate-700">
                    <Linkedin className="w-4 h-4 mr-2" />
                    LinkedIn
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Referral History */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Referral History</CardTitle>
              <CardDescription>Track your referrals and their status</CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.referrals && stats.referrals.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800">
                      <TableHead className="text-slate-400">Referred Email</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Date</TableHead>
                      <TableHead className="text-slate-400">Completed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.referrals.map((referral) => (
                      <TableRow key={referral.id} className="border-slate-800">
                        <TableCell className="text-white">{referral.referred_email}</TableCell>
                        <TableCell>{getStatusBadge(referral.status)}</TableCell>
                        <TableCell className="text-slate-400">
                          {new Date(referral.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-slate-400">
                          {referral.completed_at 
                            ? new Date(referral.completed_at).toLocaleDateString()
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No referrals yet</p>
                  <p className="text-sm mt-1">Share your link to start earning!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Credits Sidebar */}
        <div className="space-y-6">
          {/* Credit Balance */}
          <Card className="bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border-emerald-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                Credit Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <p className="text-5xl font-bold text-white mb-2">
                  {credits?.active_balance_months || 0}
                </p>
                <p className="text-emerald-400 font-medium">Free Months Available</p>
                <p className="text-slate-400 text-sm mt-1">
                  (${((credits?.active_balance_cents || 0) / 100).toFixed(2)} in credits)
                </p>
              </div>
              {credits?.active_balance_months > 0 && (
                <div className="mt-4 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                  <p className="text-sm text-emerald-400">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Credits will be automatically applied to your next invoice
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Credit History */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white text-base">Credit History</CardTitle>
            </CardHeader>
            <CardContent>
              {credits?.credits && credits.credits.length > 0 ? (
                <div className="space-y-3">
                  {credits.credits.slice(0, 5).map((credit) => (
                    <div key={credit.id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                      <div>
                        <p className="text-sm text-white">{credit.description}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(credit.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge className={
                        credit.status === 'active' 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-slate-500/20 text-slate-400'
                      }>
                        {credit.status === 'active' ? `$${(credit.amount_cents / 100).toFixed(0)}` : 'Used'}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-4">No credits yet</p>
              )}
            </CardContent>
          </Card>

          {/* Program Details */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white text-base">Program Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                <p className="text-slate-400">1 free month for each successful referral</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                <p className="text-slate-400">Referred companies also get 1 free month</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                <p className="text-slate-400">Credits stack - no limit on referrals</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                <p className="text-slate-400">Credits applied automatically to billing</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                <p className="text-slate-400">Credits never expire</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
